import type {
  AllocationConstraint,
  LadderPolicyConstraint,
  ModelConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'

import {
  AllocationConstraintType,
  AverageValueConstraintType,
  BuyUniverseConstraintType,
  DiversificationConstraintType,
  LadderPolicyConstraintType,
  PiecewiseConstraintType,
  TradeConstraintType,
  UniverseConstraintType,
} from '@moment/horizon-data-access/proposal'
import isEqual from 'lodash/isEqual'

import {
  extractExcludedFilterListIdsFromUniverseConstraint,
  extractFilterListIdsFromAllocationConstraint,
  extractIncludedFilterListIdsFromUniverseConstraint,
  isPureFilterListFilter,
} from '@/portfolio/proposal-builder/components/filter-list-risk-factor/utils'
import {
  intrinsicScopeForConfig,
  RISK_FACTOR_CONFIGS,
  retrieveScope,
} from '@/portfolio/proposal-builder/config'
import {
  extractScope,
  hasScope,
} from '@/portfolio/proposal-builder/data/helpers/constraint-scope'
import { extractFieldsFromFilter } from '@/portfolio/proposal-builder/data/helpers/extract-filter-fields'
import {
  createEmptyFieldConstraints,
  createEmptyFilterListConstraints,
  createRiskFactor,
} from '@/portfolio/proposal-builder/data/helpers/sleeve-manager'
import {
  type BondFeaturesRiskFactorConfig,
  ConfigKey,
  type ConventionalRiskFactorConfig,
  type Field,
  Priority,
  type RiskFactor,
  type RiskFactorConfig,
  type SleeveState,
} from '@/portfolio/proposal-builder/types'
import { mergePriority } from '@/utils/constraints/constraint-priority'
import { processNumPeriods } from '@/utils/validators/constraints/ladder-policy'

import { mergeSameFieldUniverseConstraints } from './consolidate-universe-constraints'
import { parsePiecewiseDiversification } from './piecewise-diversification'

// Two constraints share a scope when they restrict the same slice of the
// universe; an absent and an empty scope are the same slice (everything).
function hasSameScope(a: ModelConstraint, b: ModelConstraint): boolean {
  const sliceOf = (constraint: ModelConstraint) => {
    const scope = extractScope(constraint)
    return hasScope(scope) ? scope : undefined
  }
  return isEqual(sliceOf(a), sliceOf(b))
}

/**
 * Whether a constraint displaced from a single-valued field slot must be kept
 * in `otherConstraints`. Differently scoped constraints restrict different
 * slices, so neither can stand in for the other and the displaced one is always
 * kept. A same-scope collision is last-wins, and only ingestion overflows the
 * displaced constraint — interactive edits mean to replace it.
 */
function shouldKeepDisplaced({
  displaced,
  incoming,
  preserveDisplaced,
}: {
  displaced: ModelConstraint
  incoming: ModelConstraint
  preserveDisplaced: boolean
}): boolean {
  return preserveDisplaced || !hasSameScope(displaced, incoming)
}

function buildFieldToConfigKey(
  configs: Record<string, RiskFactorConfig>,
): Map<Field, ConfigKey> {
  const fieldToConfigKey = new Map<Field, ConfigKey>()

  Object.entries(configs).forEach(([configKey, config]) => {
    if (config.type === 'conventional') {
      const conventionalConfig = config as ConventionalRiskFactorConfig
      conventionalConfig.fields.forEach((fieldConfig) => {
        fieldToConfigKey.set(fieldConfig.field, configKey as ConfigKey)
      })
    } else if (config.type === 'bond_features') {
      const bondFeaturesConfig = config as BondFeaturesRiskFactorConfig
      bondFeaturesConfig.fields.forEach((fieldConfig) => {
        fieldToConfigKey.set(fieldConfig.field, configKey as ConfigKey)
      })
    }
  })

  return fieldToConfigKey
}

const STATIC_FIELD_TO_CONFIG_KEY = buildFieldToConfigKey(RISK_FACTOR_CONFIGS)

/**
 * Adds the constraint to the FilterLists risk factor if possible.
 * Returns true if the constraint was handled, false otherwise.
 */
function extractFilterListConstraint(
  constraint: ModelConstraint,
  riskFactors: SleeveState['riskFactors'],
): boolean {
  // Resolve the claimed slot before touching the FilterLists factor — the
  // factor must not be created (nor its priority ratcheted) by constraints
  // that don't belong to it. Mixed filters (a filter-list condition alongside
  // a regular one) stay unclaimed: the FilterLists card rebuilds filters from
  // list ids only and would destroy the regular clause on its next edit.
  const existingConstraints =
    riskFactors[ConfigKey.FilterLists]?.filterListConstraints

  let claim:
    | { slot: 'inclusion' | 'exclusion'; constraint: UniverseConstraint }
    | { slot: 'allocation'; constraint: AllocationConstraint }
    | undefined
  if (
    constraint.type === UniverseConstraintType.universe &&
    isPureFilterListFilter(constraint.filter)
  ) {
    if (
      !existingConstraints?.inclusionConstraint &&
      extractIncludedFilterListIdsFromUniverseConstraint(constraint).length > 0
    ) {
      claim = { slot: 'inclusion', constraint }
    } else if (
      !existingConstraints?.exclusionConstraint &&
      extractExcludedFilterListIdsFromUniverseConstraint(constraint).length > 0
    ) {
      claim = { slot: 'exclusion', constraint }
    }
  } else if (
    constraint.type === AllocationConstraintType.allocation &&
    isPureFilterListFilter(constraint.filter) &&
    extractFilterListIdsFromAllocationConstraint(constraint).length > 0
  ) {
    claim = { slot: 'allocation', constraint }
  }
  if (!claim) return false

  let riskFactor: RiskFactor | undefined = riskFactors[ConfigKey.FilterLists]
  if (!riskFactor) {
    riskFactor = createRiskFactor(ConfigKey.FilterLists)
    riskFactors[ConfigKey.FilterLists] = riskFactor
  }

  const priority = constraint.constraint_priority ?? Priority.Low
  const currentPriority = riskFactor.priorityLevel ?? Priority.Low
  riskFactor.priorityLevel = mergePriority(currentPriority, priority)

  if (!riskFactor.filterListConstraints) {
    riskFactor.filterListConstraints = createEmptyFilterListConstraints()
  }
  const constraints = riskFactor.filterListConstraints

  if (claim.slot === 'allocation') {
    constraints.allocationConstraints.push(claim.constraint)
  } else if (claim.slot === 'inclusion') {
    constraints.inclusionConstraint = claim.constraint
  } else {
    constraints.exclusionConstraint = claim.constraint
  }
  return true
}

// SleeveState satisfies this shape, so store mutators can pass a live sleeve
// draft directly.
export interface ConstraintTreeTarget {
  riskFactors: SleeveState['riskFactors']
  otherConstraints: ModelConstraint[]
  ladderPolicy?: LadderPolicyConstraint
}

export interface PlaceConstraintInTreeArgs {
  target: ConstraintTreeTarget
  constraint: ModelConstraint
  // Ingest preserves a same-scope constraint displaced from its single-valued
  // slot into otherConstraints; interactive create/edit keeps last-wins. A
  // differently scoped constraint is preserved either way.
  preserveDisplaced?: boolean
}

/**
 * Routes one constraint into its home slot. Single placement authority shared
 * by ingestion and the store, so a constraint lands in the same slot on save
 * as on reload. Single-valued field slots are last-wins, except that colliding
 * same-scope universe constraints merge into one and differently scoped
 * constraints are always kept (see shouldKeepDisplaced).
 */
export function placeConstraintInTree({
  target,
  constraint,
  preserveDisplaced = false,
}: PlaceConstraintInTreeArgs): void {
  const { riskFactors, otherConstraints } = target
  const fieldToConfigKey = STATIC_FIELD_TO_CONFIG_KEY

  // BuyUniverse and LadderPolicy have no case in the storage switch below —
  // without these short-circuits they would be silently dropped.
  if (constraint.type === BuyUniverseConstraintType.buy_universe) {
    otherConstraints.push(constraint)
    return
  }
  // The factor chart edits per-value caps, which can't sit beside equal weight, so an equal-weight rule stays a custom rule.
  if (
    constraint.type === DiversificationConstraintType.diversification &&
    constraint.equal_weight_tolerance != null
  ) {
    otherConstraints.push(constraint)
    return
  }
  if (constraint.type === LadderPolicyConstraintType.ladder_policy) {
    // propman may send the ladder in its `num_periods` form with no
    // `end_period`; every downstream consumer reads `end_period`, and
    // `undefined * period_length` yields NaN month offsets that make date-fns
    // throw while building the ladder's maturity bracket.
    target.ladderPolicy = processNumPeriods(constraint)
    return
  }

  const success = extractFilterListConstraint(constraint, riskFactors)
  if (success) {
    return
  }

  let fields: string[] = []

  switch (constraint.type) {
    case DiversificationConstraintType.diversification:
    case AverageValueConstraintType.average_value:
      fields = [constraint.field]
      break

    case UniverseConstraintType.universe:
    case AllocationConstraintType.allocation:
      fields = extractFieldsFromFilter(constraint.filter)
      break

    case PiecewiseConstraintType.piecewise: {
      // Account-size-tiered diversification routes to the field's risk
      // factor; any other piecewise shape stays unrecognized (no fields)
      // and falls through to otherConstraints.
      const parsed = parsePiecewiseDiversification(constraint)
      fields = parsed ? [parsed.field] : []
      break
    }

    case TradeConstraintType.trade:
      fields = []
      break
  }

  // Only a single mapped field gets a field slot; anything else stays whole
  // in otherConstraints.
  const field = fields[0] as Field
  const configKey =
    fields.length === 1 ? fieldToConfigKey.get(field) : undefined
  if (!configKey) {
    otherConstraints.push(constraint)
    return
  }

  const scope = extractScope(constraint)
  const priority = constraint.constraint_priority ?? Priority.Low

  let riskFactor = riskFactors[configKey]
  if (!riskFactor) {
    riskFactor = createRiskFactor(configKey)
    riskFactors[configKey] = riskFactor
  }

  // Capture scope from whichever constraint carries it, not just the first —
  // a leading no-scope constraint would otherwise leave riskFactor.scope empty.
  if (hasScope(scope)) {
    riskFactor.scope = scope
  }

  const currentPriority = riskFactor.priorityLevel ?? Priority.Low
  riskFactor.priorityLevel = mergePriority(currentPriority, priority)

  // FilterLists constraints are stored by extractFilterListConstraint above.
  if (riskFactor.configKey === ConfigKey.FilterLists) return

  let fieldConstraints = riskFactor.fields[field]
  if (!fieldConstraints) {
    fieldConstraints = createEmptyFieldConstraints()
    riskFactor.fields[field] = fieldConstraints
  }

  // Scope is read-only in the UI: a constraint that arrives without one is
  // coerced to the field's default so the next save serializes it explicitly.
  const coercedScope = hasScope(scope)
    ? undefined
    : (retrieveScope(field) ?? intrinsicScopeForConfig(configKey))

  switch (constraint.type) {
    case UniverseConstraintType.universe: {
      const incoming = coercedScope
        ? { ...constraint, scope: coercedScope }
        : constraint
      const displaced = fieldConstraints.universeConstraint

      // Same-scope exclusions are additive (`not_in` unions), so a colliding
      // pair coerces into one constraint instead of the later one winning and
      // the earlier restriction disappearing.
      const merged =
        displaced && hasSameScope(displaced, incoming)
          ? mergeSameFieldUniverseConstraints(displaced, incoming)
          : null
      if (merged) {
        fieldConstraints.universeConstraint = merged
        break
      }

      if (
        displaced &&
        shouldKeepDisplaced({ displaced, incoming, preserveDisplaced })
      ) {
        otherConstraints.push(displaced)
      }
      fieldConstraints.universeConstraint = incoming
      break
    }

    case DiversificationConstraintType.diversification: {
      const incoming = coercedScope
        ? { ...constraint, scope: coercedScope }
        : constraint
      const displaced = fieldConstraints.diversificationConstraint
      if (
        displaced &&
        shouldKeepDisplaced({ displaced, incoming, preserveDisplaced })
      ) {
        otherConstraints.push(displaced)
      }
      fieldConstraints.diversificationConstraint = incoming
      break
    }

    case PiecewiseConstraintType.piecewise:
      // Only reachable for tiered-diversification piecewise (parsed above).
      fieldConstraints.piecewiseDiversificationConstraint = coercedScope
        ? { ...constraint, scope: coercedScope }
        : constraint
      break

    case AverageValueConstraintType.average_value: {
      const incoming = coercedScope
        ? { ...constraint, scope: coercedScope }
        : constraint
      const displaced = fieldConstraints.averageValueConstraint
      if (
        displaced &&
        shouldKeepDisplaced({ displaced, incoming, preserveDisplaced })
      ) {
        otherConstraints.push(displaced)
      }
      fieldConstraints.averageValueConstraint = incoming
      break
    }

    case AllocationConstraintType.allocation:
      fieldConstraints.allocationConstraints.push(
        coercedScope ? { ...constraint, scope: coercedScope } : constraint,
      )
      break
  }
}

export function initStrategyToRiskFactors(constraints: ModelConstraint[]): {
  riskFactors: SleeveState['riskFactors']
  otherConstraints: ModelConstraint[]
  ladderPolicy?: LadderPolicyConstraint
} {
  const target: ConstraintTreeTarget = {
    riskFactors: {},
    otherConstraints: [],
  }

  constraints.forEach((constraint) => {
    placeConstraintInTree({ target, constraint, preserveDisplaced: true })
  })

  return target
}
